import type { OTCDealData } from '@src/types/OTC';
import { isFixedRateLoanDeal, isFloatingRateLoanDeal, isRepoDeal } from '@src/types/OTC/otcGuards';
import { formatProductType } from '@src/utils/OTC/otcUtils';
import {
  Box,
  Button,
  ButtonVariants,
  Card,
  FormControlSizes,
  HStack,
  Icon,
  IconName,
  logger,
  SecurityRenderer,
  Text,
  useJsonModal,
  useSecuritiesContext,
  VStack,
  type Security,
} from '@talos/kyoko';
import { useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { createDealJsonObject } from '../dealJsonObject';
import { InspectorText } from './InspectorText';

type OtcInspectorJsonPayload =
  | { deal: OTCDealData; security: Security | undefined }
  | { loan: OTCDealData; security: Security | undefined }
  | { repo: OTCDealData; security: Security | undefined };

interface InspectorBasicInfoProps {
  iconCurrency?: string;
  instrumentData: OTCDealData;
}

export const InspectorBasicInfo = ({ iconCurrency, instrumentData }: InspectorBasicInfoProps) => {
  const theme = useTheme();
  const { handleClickJson, jsonModal } = useJsonModal<OtcInspectorJsonPayload>();
  const { securitiesBySymbol } = useSecuritiesContext();

  const dealSecurity = useMemo(() => {
    if (!instrumentData.Symbol) {
      logger.error(new Error('OTC instrument inspector: deal has no Symbol'));
      return undefined;
    }

    const security = securitiesBySymbol.get(instrumentData.Symbol);
    if (!security) {
      logger.error(new Error(`OTC instrument inspector: deal security not found for symbol ${instrumentData.Symbol}`), {
        extra: { symbol: instrumentData.Symbol },
      });
      return undefined;
    }

    return security;
  }, [instrumentData.Symbol, securitiesBySymbol]);

  const resolvedSubProductTypeLabel = useMemo(() => {
    if (!dealSecurity) {
      return formatProductType(undefined);
    }
    return formatProductType(dealSecurity.SubProductType);
  }, [dealSecurity]);

  const onShowJson = useCallback(() => {
    const isLoan = isFixedRateLoanDeal(instrumentData) || isFloatingRateLoanDeal(instrumentData);
    const isRepo = isRepoDeal(instrumentData);
    createDealJsonObject({
      deal: instrumentData,
      propertyName: isLoan ? 'loan' : isRepo ? 'repo' : 'deal',
      securitiesBySymbol,
      handleClickJson,
    });
  }, [handleClickJson, instrumentData, securitiesBySymbol]);

  return (
    <Box data-testid="inspector-basic-info" p="spacing16" borderBottom={`1px solid ${theme.colors.gray['010']}`}>
      <VStack gap="spacing8">
        <HStack w="100%" justifyContent="flex-end">
          <Button variant={ButtonVariants.Default} size={FormControlSizes.Tiny} onClick={onShowJson}>
            <HStack color="gray.090" gap="3px">
              <Icon icon={IconName.Braces} />
              Show JSON
            </HStack>
          </Button>
        </HStack>
        <Card display="flex" flexDirection="column" w="100%">
          <VStack data-testid="inspector-basic-info-summary" gap="spacing8" w="100%" alignItems="stretch">
            <HStack justifyContent="space-between" alignItems="flex-start" gap="spacing12" w="100%">
              <Box flex={1} minWidth={0}>
                <SecurityRenderer
                  symbol={instrumentData.Symbol ?? ''}
                  security={dealSecurity}
                  currency={iconCurrency}
                  colorful
                />
              </Box>
              <Text
                color="colorTextImportant"
                data-testid="inspector-basic-info-sub-product-type"
                size="fontSizeMd"
                textAlign="right"
              >
                {resolvedSubProductTypeLabel}
              </Text>
            </HStack>
            <HStack justifyContent="space-between">
              <Text>Status</Text>
              <InspectorText>{instrumentData.Status}</InspectorText>
            </HStack>
          </VStack>
        </Card>
      </VStack>
      {jsonModal}
    </Box>
  );
};
